import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Pencil, Plus, Trash2, X } from "lucide-react";
import { AdminShell } from "../components/admin/AdminShell";
import { API, fmtPrice } from "../lib/eventUtils";

const EMPTY = {
  code: "", discount_type: "percent", value: "", commission_percent: "0",
  starts_at: "", ends_at: "", max_uses: "", eligible_event_ids: [], active: true,
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">{label}</span>
    {children}
  </label>
);

const fmtDiscount = (c) => (c.discount_type === "percent" ? `${c.value}% off` : `${fmtPrice(c.value)} off`);

export default function AdminAffiliates() {
  const [codes, setCodes] = useState(null);
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/affiliates`, { withCredentials: true });
      setCodes(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load affiliate codes");
    }
  };
  useEffect(() => {
    load();
    axios.get(`${API}/api/admin/events`, { withCredentials: true }).then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const openForm = (c) => {
    setEditing(c || "new");
    setF(c ? {
      code: c.code, discount_type: c.discount_type,
      value: c.discount_type === "fixed" ? (c.value / 100).toString() : String(c.value),
      commission_percent: String(c.commission_percent ?? 0),
      starts_at: (c.starts_at || "").slice(0, 10), ends_at: (c.ends_at || "").slice(0, 10),
      max_uses: c.max_uses ? String(c.max_uses) : "",
      eligible_event_ids: c.eligible_event_ids || [], active: !!c.active,
    } : EMPTY);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const toggleEvent = (id) => setF({
    ...f,
    eligible_event_ids: f.eligible_event_ids.includes(id)
      ? f.eligible_event_ids.filter((x) => x !== id)
      : [...f.eligible_event_ids, id],
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      code: f.code,
      discount_type: f.discount_type,
      value: f.discount_type === "fixed" ? Math.round((parseFloat(f.value) || 0) * 100) : Number(f.value) || 0,
      commission_percent: parseFloat(f.commission_percent) || 0,
      starts_at: f.starts_at ? `${f.starts_at}T00:00:00+00:00` : null,
      ends_at: f.ends_at ? `${f.ends_at}T23:59:59+00:00` : null,
      max_uses: f.max_uses ? Number(f.max_uses) : null,
      eligible_event_ids: f.eligible_event_ids,
      active: f.active,
    };
    try {
      if (editing === "new") {
        await axios.post(`${API}/api/admin/affiliates`, payload, { withCredentials: true });
        toast.success("Code created");
      } else {
        await axios.patch(`${API}/api/admin/affiliates/${editing.code_id}`, payload, { withCredentials: true });
        toast.success("Code updated");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await axios.patch(`${API}/api/admin/affiliates/${c.code_id}`, { active: !c.active }, { withCredentials: true });
      toast.success(c.active ? "Code deactivated" : "Code activated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete code ${c.code}? Attribution already recorded on signups is kept, but the code stops working immediately.`)) return;
    try {
      await axios.delete(`${API}/api/admin/affiliates/${c.code_id}`, { withCredentials: true });
      toast.success("Code deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const copyLink = (c) => {
    const target = events.find((ev) => (c.eligible_event_ids || []).includes(ev.event_id)) || events.find((ev) => ev.published && ev.price_cents > 0) || events[0];
    const url = target ? `${window.location.origin}/events/${target.slug}?ref=${c.code}` : `${window.location.origin}/events?ref=${c.code}`;
    navigator.clipboard.writeText(url);
    toast.success("Tracked link copied");
  };

  return (
    <AdminShell title="Affiliate Codes">
      <div data-testid="admin-affiliates-page">
        <div className="flex justify-end mb-6">
          <button onClick={() => openForm(null)} className="btn-volt !py-2.5 !px-5 !text-sm" data-testid="affiliates-new-button">
            <Plus size={15} /> New Code
          </button>
        </div>

        {editing && (
          <form onSubmit={submit} className="border border-volt/40 bg-[#0c0c0c] p-6 sm:p-8 mb-8" data-testid="affiliate-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-head font-extrabold uppercase text-2xl tracking-tight">{editing === "new" ? "New Affiliate Code" : `Edit: ${editing.code}`}</h2>
              <button type="button" onClick={() => setEditing(null)} className="text-zinc-500 hover:text-white" data-testid="affiliate-form-close" aria-label="Close form"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <Field label="Code *"><input required value={f.code} onChange={set("code")} placeholder="DEZ10" className="volt-input uppercase" data-testid="affiliate-form-code" /></Field>
              <Field label="Discount type">
                <select value={f.discount_type} onChange={set("discount_type")} className="volt-input" data-testid="affiliate-form-type">
                  <option value="percent">Percent (%)</option>
                  <option value="fixed">Fixed ($)</option>
                </select>
              </Field>
              <Field label={f.discount_type === "percent" ? "Discount (%) *" : "Discount ($) *"}>
                <input required type="number" min={f.discount_type === "percent" ? 1 : 0.01} max={f.discount_type === "percent" ? 100 : undefined} step={f.discount_type === "percent" ? 1 : 0.01} value={f.value} onChange={set("value")} className="volt-input" data-testid="affiliate-form-value" />
              </Field>
              <Field label="Commission (%)"><input type="number" min="0" max="100" step="0.5" value={f.commission_percent} onChange={set("commission_percent")} className="volt-input" data-testid="affiliate-form-commission" /></Field>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <Field label="Starts"><input type="date" value={f.starts_at} onChange={set("starts_at")} className="volt-input" data-testid="affiliate-form-starts" /></Field>
              <Field label="Ends"><input type="date" value={f.ends_at} onChange={set("ends_at")} className="volt-input" data-testid="affiliate-form-ends" /></Field>
              <Field label="Max uses (blank = unlimited)"><input type="number" min="1" value={f.max_uses} onChange={set("max_uses")} className="volt-input" data-testid="affiliate-form-maxuses" /></Field>
              <label className="flex items-center gap-2 mt-6 cursor-pointer">
                <input type="checkbox" checked={f.active} onChange={set("active")} data-testid="affiliate-form-active" />
                <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400">Active</span>
              </label>
            </div>
            <div className="mb-6">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2">Eligible events (none selected = all paid events)</p>
              <div className="flex flex-wrap gap-2" data-testid="affiliate-form-events">
                {events.map((ev) => (
                  <button
                    type="button"
                    key={ev.event_id}
                    onClick={() => toggleEvent(ev.event_id)}
                    className={`font-mono2 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${
                      f.eligible_event_ids.includes(ev.event_id) ? "border-volt/70 text-volt bg-volt/5" : "border-white/15 text-zinc-500 hover:text-white"
                    }`}
                    data-testid={`affiliate-form-event-${ev.slug}`}
                  >
                    {ev.title}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={busy} className="btn-volt disabled:opacity-60" data-testid="affiliate-form-save">{busy ? "Saving…" : "Save Code"}</button>
              <button type="button" onClick={() => setEditing(null)} className="btn-ghost" data-testid="affiliate-form-cancel">Cancel</button>
            </div>
          </form>
        )}

        {error && <p className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3 mb-6" data-testid="affiliates-error">{error}</p>}
        {codes === null && !error && <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-zinc-500 animate-pulse" data-testid="affiliates-loading">Loading…</p>}
        {codes !== null && codes.length === 0 && (
          <p className="text-zinc-500 text-sm border border-white/10 bg-[#0c0c0c] p-8" data-testid="affiliates-empty">
            No affiliate codes yet. Create one to start tracking clicks, uses and revenue.
          </p>
        )}

        {codes !== null && codes.length > 0 && (
          <div className="overflow-x-auto border border-white/10 bg-[#0c0c0c]">
            <table className="w-full text-sm" data-testid="affiliates-table">
              <thead>
                <tr className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/10 text-left">
                  <th className="py-2.5 px-4">Code</th>
                  <th className="py-2.5 pr-4">Discount</th>
                  <th className="py-2.5 pr-4">Commission</th>
                  <th className="py-2.5 pr-4">Window</th>
                  <th className="py-2.5 pr-4">Clicks</th>
                  <th className="py-2.5 pr-4">Uses</th>
                  <th className="py-2.5 pr-4">Revenue</th>
                  <th className="py-2.5 pr-4">Owed</th>
                  <th className="py-2.5 pr-4">Status</th>
                  <th className="py-2.5 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c, i) => (
                  <tr key={c.code_id} className="border-b border-white/5 text-zinc-300" data-testid={`affiliate-row-${i}`}>
                    <td className="py-2.5 px-4 font-mono2 font-bold text-white">{c.code}</td>
                    <td className="py-2.5 pr-4">{fmtDiscount(c)}</td>
                    <td className="py-2.5 pr-4">{c.commission_percent || 0}%</td>
                    <td className="py-2.5 pr-4 text-zinc-500 text-xs whitespace-nowrap">
                      {(c.starts_at || "").slice(0, 10) || "—"} → {(c.ends_at || "").slice(0, 10) || "—"}
                    </td>
                    <td className="py-2.5 pr-4" data-testid={`affiliate-clicks-${i}`}>{c.clicks || 0}</td>
                    <td className="py-2.5 pr-4" data-testid={`affiliate-uses-${i}`}>{c.uses || 0}{c.max_uses ? `/${c.max_uses}` : ""}</td>
                    <td className="py-2.5 pr-4 text-volt font-mono2 text-xs" data-testid={`affiliate-revenue-${i}`}>{fmtPrice(c.revenue_cents || 0)}</td>
                    <td className="py-2.5 pr-4 font-mono2 text-xs">{fmtPrice(c.commission_cents || 0)}</td>
                    <td className="py-2.5 pr-4">
                      <span className={`font-mono2 text-[9px] uppercase tracking-[0.15em] border px-2 py-0.5 ${c.active ? "border-volt/60 text-volt" : "border-white/20 text-zinc-500"}`}>
                        {c.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => copyLink(c)} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid={`affiliate-copylink-${i}`}><Copy size={12} /> Link</button>
                        <button onClick={() => openForm(c)} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid={`affiliate-edit-${i}`}><Pencil size={12} /> Edit</button>
                        <button onClick={() => toggleActive(c)} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid={`affiliate-toggle-${i}`}>{c.active ? "Deactivate" : "Activate"}</button>
                        <button onClick={() => remove(c)} className="text-zinc-600 hover:text-red-400 transition-colors px-1" data-testid={`affiliate-delete-${i}`} title="Delete code">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
